import { defineStore } from 'pinia'
import { ref } from 'vue'

export type ToastLevel = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: number
  level: ToastLevel
  message: string
}

export const DEFAULT_TIMEOUT = 4500

export const useToastStore = defineStore('toast', () => {
  const toasts = ref<Toast[]>([])
  const timers = new Map<number, ReturnType<typeof setTimeout>>()
  let nextId = 1

  function push(message: string, level: ToastLevel = 'info', timeout = DEFAULT_TIMEOUT): number {
    const id = nextId++
    toasts.value = [...toasts.value, { id, level, message }]

    if (timeout > 0) {
      timers.set(
        id,
        setTimeout(() => dismiss(id), timeout),
      )
    }

    return id
  }

  function dismiss(id: number): void {
    const timer = timers.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.delete(id)
    }
    toasts.value = toasts.value.filter((toast) => toast.id !== id)
  }

  function clear(): void {
    for (const timer of timers.values()) {
      clearTimeout(timer)
    }
    timers.clear()
    toasts.value = []
  }

  const success = (message: string) => push(message, 'success')
  const error = (message: string) => push(message, 'error', 7000)
  const info = (message: string) => push(message, 'info')
  const warning = (message: string) => push(message, 'warning')

  return { toasts, push, dismiss, clear, success, error, info, warning }
})
